import db from './db';
import type { AppSettings, LegacyAppSettings } from '../types/settings';
import type { BackendConfig } from './backends/types';
import { track } from '../analytics/posthog';

const SETTINGS_KEY = 'knitting-settings';

/**
 * Synchronous mirror of just the visual fields. Read by an inline script in
 * index.html before React mounts so the page paints with the right theme and
 * `<html lang>` — IndexedDB is async-only and would cause a flash. Always
 * written from the canonical AppSettings; never read from inside the app.
 */
const DISPLAY_CACHE_KEY = 'knittime-display';

const DEFAULTS: AppSettings = {
  autoSync: false,
  theme: 'system',
  haptics: true,
  analyticsConsent: 'pending',
};

function writeDisplayCache(settings: AppSettings): void {
  try {
    localStorage.setItem(
      DISPLAY_CACHE_KEY,
      JSON.stringify({ theme: settings.theme, language: settings.language }),
    );
  } catch {
    /* localStorage unavailable (private mode, quota) — boot script falls back to defaults */
  }
}

/**
 * Migrate the pre-backends flat shape — `{ githubPat, gistId, lastSyncEtag,
 * lastSyncedAt }` at the root — into `backend: { kind: 'gist', ... }`. New
 * shape always wins if both are present.
 */
function migrate(raw: unknown): AppSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULTS };
  const obj = raw as Partial<AppSettings> & LegacyAppSettings;

  let backend: BackendConfig | undefined = obj.backend;
  if (!backend && obj.githubPat) {
    backend = {
      kind: 'gist',
      pat: obj.githubPat,
      remoteId: obj.gistId,
      cursor: obj.lastSyncEtag,
      lastSyncedAt: obj.lastSyncedAt,
    };
    // A legacy-shape migration actually happened — fire the analytics event.
    track('migration_applied', { kind: 'settings', from_version: 0, to_version: 1 });
  }

  return {
    ...DEFAULTS,
    autoSync: obj.autoSync ?? DEFAULTS.autoSync,
    theme: obj.theme ?? DEFAULTS.theme,
    haptics: obj.haptics ?? DEFAULTS.haptics,
    backend,
    language: obj.language,
    analyticsConsent: obj.analyticsConsent ?? DEFAULTS.analyticsConsent,
    analyticsAnonId: obj.analyticsAnonId,
    analyticsInstallSalt: obj.analyticsInstallSalt,
  };
}

export async function getSettings(): Promise<AppSettings> {
  const raw = await db.getItem<unknown>(SETTINGS_KEY);
  const settings = migrate(raw);
  writeDisplayCache(settings);
  return settings;
}

type Callback = () => void;
const settingsCallbacks: Callback[] = [];

export function subscribeSettings(cb: Callback): () => void {
  settingsCallbacks.push(cb);
  return () => {
    const idx = settingsCallbacks.indexOf(cb);
    if (idx >= 0) settingsCallbacks.splice(idx, 1);
  };
}

function notifySettingsChanged(): void {
  settingsCallbacks.forEach((cb) => cb());
}

export async function saveSettings(settings: AppSettings): Promise<void> {
  try {
    await db.setItem(SETTINGS_KEY, settings);
  } catch (e) {
    if (e instanceof Error && (e.name === 'QuotaExceededError' || e.message.toLowerCase().includes('quota'))) {
      track('storage_quota_warning', {});
    }
    throw e;
  }
  writeDisplayCache(settings);
  notifySettingsChanged();
}
